import { cn } from "@plasma/utils/cn";
import type * as React from "react";

function Table({ className, ref, ...props }: React.ComponentProps<"table">) {
  return (
    <div className="relative w-full overflow-auto">
      <table
        className={cn("body-sm border-border w-full caption-bottom", className)}
        ref={ref}
        {...props}
      />
    </div>
  );
}

function TableHeader({
  className,
  ref,
  ...props
}: React.ComponentProps<"thead">) {
  return (
    <thead
      className={cn("[&_tr]:border-b-0", className)}
      ref={ref}
      {...props}
    />
  );
}

function TableBody({
  className,
  ref,
  ...props
}: React.ComponentProps<"tbody">) {
  return (
    <tbody
      className={cn("[&_tr:last-child]:border-0", className)}
      ref={ref}
      {...props}
    />
  );
}

function TableFooter({
  className,
  ref,
  ...props
}: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      className={cn(
        "subtitle-sm bg-muted/50 dark:bg-muted/50 border-t last:[&>tr]:border-b-0",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function TableRow({ className, ref, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted dark:data-[state=selected]:bg-muted border-b transition-[color,box-shadow]",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function TableHead({ className, ref, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "label text-foreground h-10 items-start justify-start px-2 text-left align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function TableCell({ className, ref, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      className={cn(
        "body-sm text-foreground p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ref,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      className={cn(
        "body-sm text-muted-foreground dark:text-muted-foreground mt-4",
        className
      )}
      ref={ref}
      {...props}
    />
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
